import { useLayoutEffect, useRef, useState } from 'react';
import type { RefObject } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Draggable } from 'gsap/Draggable';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

gsap.registerPlugin(ScrollTrigger, Draggable, ScrollToPlugin);

interface Refs {
  section: RefObject<HTMLElement | null>;
  viewport: RefObject<HTMLDivElement | null>;
  track: RefObject<HTMLDivElement | null>;
}

/**
 * Single source of truth: a local progress value p (0..1).
 * Scroll (pinned desktop), drag and click all write to p; render(p) moves the
 * track under the stationary marker and derives the active stage.
 */
export default function useProcessTimeline(count: number, refs: Refs) {
  const [active, setActive] = useState(0);
  const activeRef = useRef(0);
  const goToRef = useRef<(i: number) => void>(() => {});

  useLayoutEffect(() => {
    const section = refs.section.current;
    const viewport = refs.viewport.current;
    const track = refs.track.current;
    if (!section || !viewport || !track) return undefined;
    const last = count - 1;
    const mm = gsap.matchMedia();

    mm.add(
      {
        pinned: '(min-width: 1024px) and (min-height: 760px) and (prefers-reduced-motion: no-preference)',
        reduce: '(prefers-reduced-motion: reduce)',
        lg: '(min-width: 1024px)',
        md: '(min-width: 768px)',
      },
      (ctx) => {
        const { pinned, reduce, lg, md } = ctx.conditions as Record<string, boolean>;
        const fill = track.querySelector<HTMLElement>('[data-fill]');
        const state = { p: activeRef.current / last, markerX: 0, span: 1, dragging: false };
        let st: ScrollTrigger | null = null;
        let tween: gsap.core.Tween | null = null;

        const measure = () => {
          const vw = viewport.clientWidth;
          const gap = lg ? 320 : md ? 260 : 180;
          state.markerX = Math.round(lg ? vw * 0.22 : md ? vw * 0.18 : 20);
          state.span = gap * last;
          track.style.setProperty('--gap', `${gap}px`);
          viewport.style.setProperty('--marker', `${state.markerX}px`);
        };

        const render = (p: number) => {
          state.p = gsap.utils.clamp(0, 1, p);
          if (!state.dragging) gsap.set(track, { x: state.markerX - state.p * state.span });
          if (fill) gsap.set(fill, { width: state.p * state.span });
          const idx = Math.round(state.p * last);
          if (idx !== activeRef.current) {
            activeRef.current = idx;
            setActive(idx);
          }
        };

        measure();
        render(state.p);

        const html = document.documentElement;
        const prevBehavior = html.style.scrollBehavior;
        if (pinned) {
          html.style.scrollBehavior = 'auto';
          st = ScrollTrigger.create({
            trigger: section,
            start: 'top top',
            end: () => `+=${Math.round(window.innerHeight * 2.2)}`,
            pin: true,
            anticipatePin: 1,
            onUpdate: (self) => render(self.progress),
            onRefresh: (self) => { measure(); render(self.progress); },
            snap: {
              snapTo: (v: number) => (state.dragging ? v : Math.round(v * last) / last),
              duration: { min: 0.2, max: 0.45 },
              delay: 0.12,
              ease: 'power2.inOut',
            },
          });
        }

        const scrollFor = (p: number) => (st ? st.start + p * (st.end - st.start) : 0);

        const goTo = (i: number) => {
          const p = gsap.utils.clamp(0, last, i) / last;
          tween?.kill();
          if (st) {
            tween = gsap.to(window, {
              scrollTo: { y: scrollFor(p), autoKill: false },
              duration: reduce ? 0 : 0.7,
              ease: 'power2.inOut',
              overwrite: true,
            });
          } else {
            const o = { p: state.p };
            tween = gsap.to(o, {
              p,
              duration: reduce ? 0 : 0.6,
              ease: 'power2.inOut',
              onUpdate: () => render(o.p),
            });
          }
        };
        goToRef.current = goTo;

        const progressFromX = (x: number) => (state.markerX - x) / state.span;

        const [drag] = Draggable.create(track, {
          type: 'x',
          cursor: 'grab',
          activeCursor: 'grabbing',
          dragClickables: true,
          minimumMovement: 4,
          onPress() {
            tween?.kill();
            this.applyBounds({ minX: state.markerX - state.span, maxX: state.markerX });
          },
          onDragStart() { state.dragging = true; },
          onDrag() {
            const p = progressFromX(this.x);
            if (st) window.scrollTo(0, scrollFor(p));
            else render(p);
          },
          onDragEnd() {
            state.dragging = false;
            goTo(Math.round(gsap.utils.clamp(0, 1, progressFromX(this.x)) * last));
          },
        });

        const onResize = () => { measure(); render(state.p); };
        if (!st) window.addEventListener('resize', onResize);

        return () => {
          window.removeEventListener('resize', onResize);
          html.style.scrollBehavior = prevBehavior;
          tween?.kill();
          drag?.kill();
          gsap.set(track, { clearProps: 'transform' });
        };
      },
    );

    return () => mm.revert();
  }, [count, refs.section, refs.viewport, refs.track]);

  return { active, goTo: (i: number) => goToRef.current(i) };
}